import React from 'react';
import styled, { css } from 'styled-components';
import { LearnerProgress } from '.';
import { LEARNER_STATUS } from '../../../api/admin';
import { toast } from 'react-toastify';
import { createColumnHelper, Row, SortingFn } from '@tanstack/react-table';
import { SmallPortalTooltip } from '../../Tooltip';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import NameCellContents from '../Cells/Name';
import StatusCell from './StatusCell';
import { TooltipContent, TooltipDetail, TooltipTitle } from './Tooltip';
import TopicsCell from './TopicsCell';
import DifficultyCell from './DifficultyCell';
import MentorOfficeHourCell from './MentorOfficeHourCell';
import OnboardingAnswersCell from './OnboardingAnswersCell';
import MentorNotesCell from './MentorNotesCell';
import ActivityStatsCell from './ActivityStatsCell';
import {
  processProgressDataForCSVDownload,
  convertToCSV,
} from './processProgressDataForCSVDownload';
import ProgressCell from './ProgressCell';
import { Table } from '../../Table/CompactTable';
import {
  CollapsibleSectionFooter,
  CollapsibleSectionFooterButton,
  CollapsibleSectionLabel,
} from '../CollapsibleSection';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const TableWrapper = styled.div<{ enableVirtualization?: boolean }>`
  .table-scroll-wrapper {
    max-height: 60vh;
    ${({ enableVirtualization }) =>
      enableVirtualization &&
      css`
        height: 60vh;
      `}
  }
  .cohort-progress-table {
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
    }

    th {
      border-radius: 0 !important;
    }
  }
`;

interface CohortTableProps {
  learners: LearnerProgress[];
  cohortId: string;
  learnerRoles: Record<string, string | null>;
}

const CohortProgress: React.FC<CohortTableProps> = ({
  learners,
  learnerRoles,
}) => {
  const columnHelper = createColumnHelper<LearnerProgress>();

  // Custom sorting function for planProgress
  const planProgressSortingFn: SortingFn<LearnerProgress> = (
    rowA: Row<LearnerProgress>,
    rowB: Row<LearnerProgress>
  ): number => {
    const a = rowA.original.planProgress;
    const b = rowB.original.planProgress;
    const statusA = rowA.original.status;
    const statusB = rowB.original.status;
    const isInvitedA =
      statusA === LEARNER_STATUS.INVITED ||
      statusA === LEARNER_STATUS.INVITE_EXPIRED;
    const isInvitedB =
      statusB === LEARNER_STATUS.INVITED ||
      statusB === LEARNER_STATUS.INVITE_EXPIRED;
    const valueA = !isInvitedA && typeof a === 'number' ? a : -1;
    const valueB = !isInvitedB && typeof b === 'number' ? b : -1;
    return valueA - valueB;
  };

  // Custom sorting function for difficulty
  const difficultySortingFn: SortingFn<LearnerProgress> = (
    rowA: Row<LearnerProgress>,
    rowB: Row<LearnerProgress>,
    columnId: string
  ): number => {
    type DifficultyType = { average: number | null | undefined };
    const diffA = (rowA.getValue(columnId) as DifficultyType | undefined)
      ?.average;
    const diffB = (rowB.getValue(columnId) as DifficultyType | undefined)
      ?.average;
    const valueA = typeof diffA === 'number' ? diffA : -1;
    const valueB = typeof diffB === 'number' ? diffB : -1;
    return valueA - valueB;
  };

  // Custom sorting function for mentor
  const mentorSortingFn: SortingFn<LearnerProgress> = (
    rowA: Row<LearnerProgress>,
    rowB: Row<LearnerProgress>,
    columnId: string
  ): number => {
    type MentorType = { name?: string | null };
    const mentorA = rowA.getValue(columnId) as MentorType | undefined;
    const mentorB = rowB.getValue(columnId) as MentorType | undefined;
    const hasMentorA = mentorA && mentorA.name;
    const hasMentorB = mentorB && mentorB.name;
    if (hasMentorA && hasMentorB) {
      return (mentorA.name || '').localeCompare(mentorB.name || '', undefined, {
        sensitivity: 'base',
      });
    }
    if (hasMentorA && !hasMentorB) return -1;
    if (!hasMentorA && hasMentorB) return 1;
    return 0;
  };

  const columns = [
    columnHelper.accessor('name', {
      header: () => 'Learner',
      cell: (info) => (
        <NameCellContents
          name={info.getValue()}
          email={info.row.original.email}
          role={learnerRoles[info.row.original.learnerId]}
          tooltipId={`learner-${info.row.id}`}
          isBold
        />
      ),
      meta: {
        styles: {
          maxWidth: '250px',
          minWidth: '200px',
        },
      },
    }),
    columnHelper.accessor('status', {
      header: () => 'Status',
      cell: StatusCell,
      meta: {
        styles: {
          minWidth: '150px',
          maxWidth: '165px',
        },
      },
    }),
    columnHelper.accessor('topics', {
      header: () => 'Modules / Projects',
      cell: TopicsCell,
      meta: {
        styles: { maxWidth: '500px', minWidth: '300px' },
      },
      enableSorting: false,
    }),
    columnHelper.accessor('planProgress', {
      header: () => 'Progress',
      cell: ProgressCell,
      meta: {
        styles: {
          minWidth: '135px',
          maxWidth: '135px',
          textAlign: 'right',
        },
      },
      enableSorting: true,
      sortingFn: planProgressSortingFn,
    }),
    columnHelper.accessor('progress.difficulty', {
      header: () => 'Difficulty',
      cell: DifficultyCell,
      meta: {
        styles: {
          minWidth: '135px',
          maxWidth: '135px',
          textAlign: 'right',
        },
      },
      sortingFn: difficultySortingFn,
    }),
    columnHelper.accessor('mentor', {
      header: () => 'Mentor',
      cell: MentorOfficeHourCell,
      meta: {
        styles: {
          minWidth: '200px',
          maxWidth: '200px',
        },
      },
      sortingFn: mentorSortingFn,
    }),
    columnHelper.accessor('lastActiveDate', {
      header: () => 'Last Active',
      cell: (info) => {
        const lastActiveDate = info.getValue();

        if (!lastActiveDate) {
          return (
            <div
              style={{ textAlign: 'center', fontStyle: 'italic', opacity: 0.6 }}
            >
              -
            </div>
          );
        }

        const uniqueId = `last-active-tooltip-${info.row.id}`;

        return (
          <div data-tooltip-id={uniqueId}>
            {dayjs(lastActiveDate).fromNow()}
            <SmallPortalTooltip
              anchorSelect={`[data-tooltip-id="${uniqueId}"]`}
            >
              <TooltipContent>
                <TooltipTitle>Last Active</TooltipTitle>
                <TooltipDetail>
                  {dayjs(lastActiveDate).format('MMM D, YYYY HH:mm UTC')}
                </TooltipDetail>
              </TooltipContent>
            </SmallPortalTooltip>
          </div>
        );
      },
      meta: {
        styles: {
          minWidth: '180px',
          maxWidth: '180px',
        },
      },
    }),

    columnHelper.display({
      id: 'mentor-notes',
      header: () => 'Mentor Notes',
      cell: MentorNotesCell,
      meta: {
        styles: {
          minWidth: '250px',
          maxWidth: '250px',
        },
      },
    }),
    columnHelper.display({
      id: 'activity-stats',
      header: () => 'Activity Stats',
      cell: ActivityStatsCell,
      meta: {
        styles: {
          minWidth: '180px',
          maxWidth: '180px',
        },
      },
    }),

    columnHelper.accessor('onboardingAnswers', {
      header: () => 'Onboarding ',
      cell: OnboardingAnswersCell,
      meta: {
        styles: {
          minWidth: '150px',
          maxWidth: '150px',
        },
      },
      enableSorting: false,
    }),
  ];

  const handleDownloadCsv = () => {
    const csvData = processProgressDataForCSVDownload(learners);

    const csvContent = convertToCSV(csvData);

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `cohort-progress-${dayjs().format('YYYY-MM-DD')}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('CSV downloaded successfully!');
  };

  const shouldVirtualize = learners.length > 20;

  return (
    <TableWrapper enableVirtualization={shouldVirtualize}>
      <Table
        className="cohort-progress-table"
        data={learners}
        getRowId={(row) => row.sessionId.toString()}
        columns={columns}
        initialPinning={{
          left: ['name'],
        }}
        initialSorting={[{ id: 'planProgress', desc: true }]}
        footer={
          <CollapsibleSectionFooter>
            <CollapsibleSectionLabel>
              {learners.length} {learners.length === 1 ? 'Learner' : 'Learners'}
            </CollapsibleSectionLabel>
            <CollapsibleSectionFooterButton
              label="Download CSV"
              onClick={handleDownloadCsv}
              faIcon="fas fa-download"
            />
          </CollapsibleSectionFooter>
        }
        enableVirtualization={shouldVirtualize}
        virtualizationRowHeight={50}
      />
    </TableWrapper>
  );
};

export default CohortProgress;
